import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { Files } from '~/eds/src/components/Files/Files';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { getUserClientInfo } from '~/utils/user';

import { DEFAULT_ACCEPTED_FILES } from '../constants';
import { BasePartyModalProps } from '../types';

export const UploadPartyModal = ({ onHide, clientId }: BasePartyModalProps) => {
  const user = useCurrentUser();
  const intl = useIntl();

  const { toast } = useToast();

  const [files, setFiles] = useState<File[]>([]);
  const [isInternal, setIsInternal] = useState(true);

  const [uploadParty, { isLoading: isUploading }] =
    api.endpoints.uploadParty.useMutation();

  const handleRemoveFile = (
    file: File,
    removedFileIndex: number | undefined,
  ) => {
    if (removedFileIndex !== undefined) {
      const updatedFiles = files.filter((_file, i) => removedFileIndex !== i);
      setFiles(updatedFiles);
    }
  };

  const children = (
    <Layout preset="sections">
      <RadioGroup
        name="Upload Party"
        title={intl.formatMessage({
          id: 'UploadPartyModal.selectPartyTitle',
          defaultMessage: 'Select Party',
          description: 'Title for party selection radio group',
        })}
        options={[
          {
            label: intl.formatMessage({
              id: 'UploadPartyModal.internalPartiesLabel',
              defaultMessage: 'Internal Parties',
              description: 'Label for internal parties option',
            }),
            value: true,
            info: intl.formatMessage({
              id: 'UploadPartyModal.internalPartiesInfo',
              defaultMessage: 'Upload a party file to you internal list.',
              description: 'Information text for internal parties option',
            }),
          },
          {
            label: intl.formatMessage({
              id: 'UploadPartyModal.counterPartiesLabel',
              defaultMessage: 'Counter Parties',
              description: 'Label for counter parties option',
            }),
            value: false,
            info: intl.formatMessage({
              id: 'UploadPartyModal.counterPartiesInfo',
              defaultMessage: 'Upload a party file to your counter list.',
              description: 'Information text for counter parties option',
            }),
          },
        ]}
        onChange={(updatedValue) => {
          setIsInternal(updatedValue ?? false);
        }}
        value={isInternal}
      />
      <FileInput
        accept={DEFAULT_ACCEPTED_FILES}
        isMulti={true}
        name="upload"
        onChange={(updatedFiles) => {
          setFiles(updatedFiles ?? []);
        }}
        value={null}
      />
      <Files
        files={files}
        onRemoveFile={handleRemoveFile}
        title={intl.formatMessage({
          id: 'UploadPartyModal.partyListsTitle',
          defaultMessage: 'Party Lists',
          description: 'Title for the files list section',
        })}
      />
    </Layout>
  );

  return (
    <Modal
      children={children}
      enableHideOnPrimaryAction={true}
      isVisible={true}
      primaryAction={{
        disabled: files.length === 0,
        isLoading: isUploading,
        onClick: () => {
          trackSegment('User Uploads Document', {
            fileNames: files.map((file) => file.name),
            groupId: clientId,
            userId: getUserClientInfo(user),
          });
          uploadParty({
            clientId,
            isInternal,
            files,
          })
            .unwrap()
            .then(() => {
              toast({
                message: intl.formatMessage({
                  id: 'UploadPartyModal.successMessage',
                  defaultMessage: 'Your file(s) has been added successfully.',
                  description: 'Success message when files are uploaded',
                }),
                status: 'success',
              });
            })
            .catch((error) => {
              toast({
                message: `${error.response.data.detail}`,
                status: 'danger',
              });
            });
          onHide();
        },
        text: intl.formatMessage({
          id: 'UploadPartyModal.uploadPartyButton',
          defaultMessage: 'Upload Party',
          description: 'Primary action button text',
        }),
        tooltip:
          files.length === 0
            ? intl.formatMessage({
                id: 'UploadPartyModal.uploadFileTooltip',
                defaultMessage: 'Please upload a file to continue.',
                description: 'Tooltip when no files are selected',
              })
            : intl.formatMessage({
                id: 'UploadPartyModal.uploadPartiesSuccess',
                defaultMessage: 'Upload your Parties',
                description: 'Tooltip when files are ready to upload',
              }),
        variant: 'primary',
      }}
      onHide={onHide}
      title={intl.formatMessage({
        id: 'UploadPartyModal.modalTitle',
        defaultMessage: 'Upload',
        description: 'Modal title for party upload',
      })}
    />
  );
};
